import { lazy } from 'react';

const RELOAD_FLAG = 'chunk_reload_attempted';

// Bots and prerender services must never trigger the reload path: Netlify's
// prerenderer treats any window.location change during load as a client-side
// redirect and returns HTTP 500 to the crawler (observed with AdsBot and
// Googlebot — it disapproved Google Ads destinations). Crawlers don't carry
// stale sessions anyway, so for them we fall through to the error boundary.
const isBotOrPrerender = () =>
  typeof navigator === 'undefined' ||
  /bot|crawl|spider|slurp|prerender|headless|lighthouse/i.test(
    navigator.userAgent
  );

// Prerender/webview environments may also block storage access entirely —
// treat that the same as "already retried" so we never loop.
const hasReloadFlag = () => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) !== null;
  } catch (e) {
    return true;
  }
};

const setReloadFlag = () => {
  try {
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch (e) {
    /* storage unavailable — reload guard handled by hasReloadFlag */
  }
};

// After a redeploy, users with an open session request lazy chunks whose
// hashed filenames no longer exist — the import rejects and the error
// boundary shows "Oops!" on an otherwise healthy site. One hard reload
// fetches the fresh index.html and fixes it.
//
// The flag is deliberately never cleared while the tab is open (sessionStorage
// dies with the tab): clearing it on a later successful import would re-arm
// the reload while another chunk is still failing, producing a reload loop.
// Worst case with this design: a second stale-chunk event in the same tab
// falls through to the error boundary, which offers a manual refresh.
const lazyWithRetry = (importFn) =>
  lazy(() =>
    importFn().catch((error) => {
      if (!isBotOrPrerender() && !hasReloadFlag()) {
        setReloadFlag();
        window.location.reload();
        // Never resolves — the page is reloading.
        return new Promise(() => {});
      }
      throw error;
    })
  );

export default lazyWithRetry;
